/**
 * Effects Manager
 * Handles creation, configuration, and routing of audio effects
 */

import * as Tone from 'tone';
import type {
  Effect,
  EffectType,
  EffectsChain,
  EffectParametersMap,
  ReverbParameters,
  DelayParameters,
  DEFAULT_EFFECT_PARAMETERS,
} from '~/types/effects';

export class EffectsManager {
  private effectsChains: Map<number, EffectsChain> = new Map();
  private effectNodes: Map<string, any> = new Map(); // effect.id -> Tone effect node
  private inputNodes: Map<number, Tone.Gain> = new Map(); // deviceIndex -> input gain
  private outputNodes: Map<number, Tone.Gain> = new Map(); // deviceIndex -> output gain

  constructor() {
    // Initialize with Tone.js context
    this.ensureAudioContext();
  }

  private ensureAudioContext() {
    // Ensure Tone.js is initialized
    if (Tone.getContext().state === 'suspended') {
      Tone.start();
    }
  }

  /**
   * Create an effects chain for a device
   */
  createEffectsChain(deviceIndex: number): EffectsChain {
    const chain: EffectsChain = {
      deviceIndex,
      effects: [],
      bypass: false,
    };

    // Create input/output gain nodes for this chain
    const inputGain = new Tone.Gain(1).toDestination();
    const outputGain = new Tone.Gain(1);

    this.inputNodes.set(deviceIndex, inputGain);
    this.outputNodes.set(deviceIndex, outputGain);

    // Connect input to output initially (no effects)
    inputGain.connect(outputGain);

    this.effectsChains.set(deviceIndex, chain);
    return chain;
  }

  /**
   * Add an effect to a device's chain
   */
  addEffect(deviceIndex: number, type: EffectType, parameters?: Partial<EffectParametersMap[typeof type]>): Effect {
    let chain = this.effectsChains.get(deviceIndex);
    if (!chain) {
      chain = this.createEffectsChain(deviceIndex);
    }

    // Create effect with default parameters
    const effect: Effect = {
      id: `${type}-${Date.now()}-${Math.random()}`,
      type,
      name: this.getEffectDisplayName(type),
      enabled: true,
      parameters: {
        ...this.getDefaultParameters(type),
        ...parameters,
      } as any,
    };

    // Create Tone.js effect node
    const node = this.createToneEffect(effect);
    if (node) {
      this.effectNodes.set(effect.id, node);
      effect.node = node;
    }

    // Add to chain
    chain.effects.push(effect);

    // Rebuild audio routing
    this.rebuildRouting(deviceIndex);

    return effect;
  }

  /**
   * Remove an effect from a chain
   */
  removeEffect(deviceIndex: number, effectId: string) {
    const chain = this.effectsChains.get(deviceIndex);
    if (!chain) return;

    const effectIndex = chain.effects.findIndex(e => e.id === effectId);
    if (effectIndex === -1) return;

    // Dispose of Tone.js node
    const node = this.effectNodes.get(effectId);
    if (node && node.dispose) {
      node.dispose();
    }
    this.effectNodes.delete(effectId);

    // Remove from chain
    chain.effects.splice(effectIndex, 1);

    // Rebuild routing
    this.rebuildRouting(deviceIndex);
  }

  /**
   * Update effect parameters
   */
  updateEffectParameters<T extends EffectType>(
    deviceIndex: number,
    effectId: string,
    parameters: Partial<EffectParametersMap[T]>
  ) {
    const chain = this.effectsChains.get(deviceIndex);
    if (!chain) return;

    const effect = chain.effects.find(e => e.id === effectId);
    if (!effect) return;

    // Update parameters
    Object.assign(effect.parameters, parameters);

    // Apply to Tone.js node
    const node = this.effectNodes.get(effectId);
    if (node) {
      this.applyParametersToNode(effect, node);
    }
  }

  /**
   * Toggle effect bypass
   */
  toggleEffectBypass(deviceIndex: number, effectId: string) {
    const chain = this.effectsChains.get(deviceIndex);
    if (!chain) return;

    const effect = chain.effects.find(e => e.id === effectId);
    if (!effect) return;

    effect.parameters.bypass = !effect.parameters.bypass;

    // Rebuild routing to apply bypass
    this.rebuildRouting(deviceIndex);
  }

  /**
   * Get the audio input node for a device
   */
  getInputNode(deviceIndex: number): Tone.Gain | undefined {
    return this.inputNodes.get(deviceIndex);
  }

  /**
   * Get the audio output node for a device
   */
  getOutputNode(deviceIndex: number): Tone.Gain | undefined {
    return this.outputNodes.get(deviceIndex);
  }

  /**
   * Create a Tone.js effect node based on effect type
   */
  private createToneEffect(effect: Effect): any {
    switch (effect.type) {
      case 'reverb':
        const reverbParams = effect.parameters as ReverbParameters;
        const reverb = new Tone.Reverb({
          decay: reverbParams.decay,
          preDelay: reverbParams.preDelay,
          wet: reverbParams.wet,
        });
        // Generate impulse response
        reverb.generate();
        return reverb;

      case 'delay':
        const delayParams = effect.parameters as DelayParameters;
        return new Tone.FeedbackDelay({
          delayTime: delayParams.delayTime / 1000, // Convert ms to seconds
          feedback: delayParams.feedback,
          wet: delayParams.wet,
        });

      // Add more effects as needed
      default:
        console.warn(`Effect type ${effect.type} not implemented yet`);
        return null;
    }
  }

  /**
   * Apply parameter changes to a Tone.js node
   */
  private applyParametersToNode(effect: Effect, node: any) {
    if (!node) return;

    switch (effect.type) {
      case 'reverb':
        const reverbParams = effect.parameters as ReverbParameters;
        if (node.decay) node.decay = reverbParams.decay;
        if (node.preDelay) node.preDelay = reverbParams.preDelay;
        if (node.wet) node.wet.value = reverbParams.wet;
        break;

      case 'delay':
        const delayParams = effect.parameters as DelayParameters;
        if (node.delayTime) node.delayTime.value = delayParams.delayTime / 1000;
        if (node.feedback) node.feedback.value = delayParams.feedback;
        if (node.wet) node.wet.value = delayParams.wet;
        break;

      // Add more parameter mappings as needed
    }
  }

  /**
   * Rebuild the audio routing for a device's effects chain
   */
  private rebuildRouting(deviceIndex: number) {
    const chain = this.effectsChains.get(deviceIndex);
    const inputNode = this.inputNodes.get(deviceIndex);
    const outputNode = this.outputNodes.get(deviceIndex);

    if (!chain || !inputNode || !outputNode) return;

    // Disconnect everything
    inputNode.disconnect();
    chain.effects.forEach(effect => {
      const node = this.effectNodes.get(effect.id);
      if (node) node.disconnect();
    });

    // Build new routing
    let lastNode: any = inputNode;

    // Connect effects in series
    const activeEffects = chain.effects.filter(e =>
      e.enabled && !e.parameters.bypass && this.effectNodes.has(e.id)
    );

    activeEffects.forEach(effect => {
      const node = this.effectNodes.get(effect.id);
      if (node) {
        lastNode.connect(node);
        lastNode = node;
      }
    });

    // Connect to output
    lastNode.connect(outputNode);
    outputNode.toDestination();
  }

  /**
   * Get default parameters for an effect type
   */
  private getDefaultParameters(type: EffectType): EffectParametersMap[typeof type] {
    // Import defaults from our types file
    const defaults = {
      reverb: {
        bypass: false,
        wet: 0.3,
        roomSize: 0.7,
        decay: 1.5,
        preDelay: 0.01,
        dampening: 0.5,
      },
      delay: {
        bypass: false,
        wet: 0.25,
        delayTime: 250,
        feedback: 0.3,
        syncToBpm: false,
        subdivision: '8n' as const,
      },
      chorus: {
        bypass: false,
        wet: 0.5,
        frequency: 1.5,
        delayTime: 3.5,
        depth: 0.7,
        spread: 180,
      },
      distortion: {
        bypass: false,
        wet: 1.0,
        distortion: 0.4,
        oversample: '2x' as const,
      },
      filter: {
        bypass: false,
        wet: 1.0,
        type: 'lowpass' as const,
        frequency: 2000,
        Q: 1,
        gain: 0,
      },
      compressor: {
        bypass: false,
        wet: 1.0,
        threshold: -24,
        ratio: 4,
        knee: 30,
        attack: 0.003,
        release: 0.25,
      },
    };

    return defaults[type] as any;
  }

  /**
   * Get display name for an effect type
   */
  private getEffectDisplayName(type: EffectType): string {
    const names: Record<EffectType, string> = {
      reverb: 'Reverb',
      delay: 'Delay',
      chorus: 'Chorus',
      distortion: 'Distortion',
      filter: 'Filter',
      compressor: 'Compressor',
    };
    return names[type] || type;
  }

  /**
   * Dispose of all resources
   */
  dispose() {
    // Dispose all effect nodes
    this.effectNodes.forEach(node => {
      if (node && node.dispose) {
        node.dispose();
      }
    });

    // Dispose gain nodes
    this.inputNodes.forEach(node => node.dispose());
    this.outputNodes.forEach(node => node.dispose());

    // Clear maps
    this.effectsChains.clear();
    this.effectNodes.clear();
    this.inputNodes.clear();
    this.outputNodes.clear();
  }

  /**
   * Get effects chain for a device
   */
  getEffectsChain(deviceIndex: number): EffectsChain | undefined {
    return this.effectsChains.get(deviceIndex);
  }

  /**
   * Enable/disable entire effects chain for a device
   */
  toggleChainBypass(deviceIndex: number) {
    const chain = this.effectsChains.get(deviceIndex);
    if (!chain) return;

    chain.bypass = !chain.bypass;
    this.rebuildRouting(deviceIndex);
  }
}